import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Grid, TextField, MenuItem, Button, Typography,
  IconButton, Alert, Divider, Chip, CircularProgress,
} from '@mui/material';
import { PhotoCamera, AddPhotoAlternate, Close, Save } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';
import AuthImage from '../common/AuthImage';
import { compressImage } from './imageUtils';

const CURRENCIES = ['USD', 'CNY', 'EUR', 'INR', 'HKD'];
const INTEREST = [
  { v: 'high', label: 'High — follow up' },
  { v: 'medium', label: 'Medium' },
  { v: 'low', label: 'Low — just noting' },
];
const STATUSES = [
  { v: 'new', label: 'New' },
  { v: 'following_up', label: 'Following up' },
  { v: 'sample_requested', label: 'Sample requested' },
  { v: 'sourced', label: 'Sourced' },
  { v: 'rejected', label: 'Not pursuing' },
];
// Remembered between entries so you don't retype the show name at every booth.
const LAST_SHOW_KEY = 'fx_last_exhibition';

export default function ExhibitionForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    product_name: '', category: '', model: '', supplier_name: '', booth_no: '',
    contact_person: '', contact_phone: '', contact_email: '', wechat: '', website: '',
    price: '', currency: 'USD', moq: '', unit: '', lead_time: '',
    specifications: '', notes: '',
    exhibition: localStorage.getItem(LAST_SHOW_KEY) || '',
    seen_date: today, interest: 'medium', status: 'new',
  });
  const [newPhotos, setNewPhotos] = useState([]);   // { file, preview }
  const [existing, setExisting] = useState([]);     // saved photos (edit mode)
  const [busy, setBusy] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    api.get(`/exhibition-products/${id}`).then(r => {
      const d = r.data.data || {};
      setForm(f => {
        const next = { ...f };
        Object.keys(f).forEach(k => { if (d[k] !== null && d[k] !== undefined) next[k] = String(d[k]); });
        next.price = d.price != null ? String(parseFloat(d.price)) : '';
        next.seen_date = d.seen_date || '';
        next.currency = d.currency || 'USD';
        next.interest = d.interest || 'medium';
        next.status = d.status || 'new';
        return next;
      });
      setExisting(d.photos || []);
    }).catch(() => setError('Could not load this product'));
  }, [id]);

  // Release preview object URLs when the component goes away.
  useEffect(() => () => newPhotos.forEach(p => URL.revokeObjectURL(p.preview)), [newPhotos]);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setPreparing(true);
    try {
      const prepared = [];
      for (const f of files) {
        const small = await compressImage(f);
        prepared.push({ file: small, preview: URL.createObjectURL(small) });
      }
      setNewPhotos(prev => [...prev, ...prepared].slice(0, 12));
    } finally { setPreparing(false); }
  };

  const dropNew = (idx) => setNewPhotos(prev => {
    const p = prev[idx];
    if (p) URL.revokeObjectURL(p.preview);
    return prev.filter((_, i) => i !== idx);
  });

  const deleteExisting = async (photoId) => {
    try {
      await api.delete(`/exhibition-products/photos/${photoId}`);
      setExisting(prev => prev.filter(p => p.id !== photoId));
    } catch { setError('Could not remove that photo'); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const anyDetail = Object.entries(form).some(([k, v]) =>
      !['currency', 'interest', 'status', 'seen_date', 'exhibition'].includes(k) && String(v).trim() !== '');
    if (!anyDetail && !newPhotos.length && !existing.length) {
      setError('Add at least a photo or one detail'); return;
    }
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
      newPhotos.forEach(p => fd.append('photos', p.file, p.file.name));
      if (form.exhibition) localStorage.setItem(LAST_SHOW_KEY, form.exhibition);
      if (id) await api.put(`/exhibition-products/${id}`, fd);
      else    await api.post('/exhibition-products', fd);
      navigate('/sourcing/exhibition');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save — check your connection and try again');
    } finally { setBusy(false); }
  };

  const f = (field, extra = {}) => ({
    value: form[field],
    onChange: (e) => setForm({ ...form, [field]: e.target.value }),
    fullWidth: true, size: 'small', ...extra,
  });

  return (
    <Box component="form" onSubmit={handleSave}>
      <PageHeader
        title={id ? 'Edit Product' : 'New Product Find'}
        subtitle="Snap it now, fill in the details later — every field is optional"
        breadcrumbs={[{ label: 'Exhibition Finds', path: '/sourcing/exhibition' }, { label: id ? 'Edit' : 'New' }]}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => navigate('/sourcing/exhibition')}>Cancel</Button>
            <Button type="submit" variant="contained" startIcon={<Save />} disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── Photos first: this is what you actually do at a booth ───────── */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>Photos</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            <Button variant="contained" startIcon={<PhotoCamera />} onClick={() => cameraRef.current?.click()}>
              Take Photo
            </Button>
            <Button variant="outlined" startIcon={<AddPhotoAlternate />} onClick={() => galleryRef.current?.click()}>
              Choose Photos
            </Button>
            {preparing && <Chip size="small" icon={<CircularProgress size={12} />} label="Preparing…" />}
          </Box>
          {/* capture=environment opens the rear camera straight away on a phone */}
          <input ref={cameraRef} hidden type="file" accept="image/*" capture="environment"
            onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
          <input ref={galleryRef} hidden type="file" accept="image/*" multiple
            onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />

          {(existing.length > 0 || newPhotos.length > 0) ? (
            <Grid container spacing={1}>
              {existing.map(p => (
                <Grid item xs={4} sm={3} md={2} key={`e${p.id}`}>
                  <Box sx={{ position: 'relative' }}>
                    <AuthImage photoId={p.id} height={110} alt={p.original_name || 'photo'} />
                    <IconButton size="small" onClick={() => deleteExisting(p.id)}
                      sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,.55)', color: '#fff',
                            '&:hover': { bgcolor: 'rgba(0,0,0,.75)' } }}>
                      <Close sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
              {newPhotos.map((p, i) => (
                <Grid item xs={4} sm={3} md={2} key={`n${i}`}>
                  <Box sx={{ position: 'relative' }}>
                    <Box component="img" src={p.preview} alt=""
                      sx={{ width: '100%', height: 110, objectFit: 'cover', borderRadius: 2, display: 'block' }} />
                    <IconButton size="small" onClick={() => dropNew(i)}
                      sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,.55)', color: '#fff',
                            '&:hover': { bgcolor: 'rgba(0,0,0,.75)' } }}>
                      <Close sx={{ fontSize: 15 }} />
                    </IconButton>
                    <Chip label="new" size="small" color="primary"
                      sx={{ position: 'absolute', bottom: 4, left: 4, height: 18, fontSize: 10 }} />
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No photos yet — tap <strong>Take Photo</strong> to capture the product or its spec sheet.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Product</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField label="Product Name" {...f('product_name')} placeholder="e.g. UHF ceiling antenna" autoFocus={!id} />
                </Grid>
                <Grid item xs={12} sm={6}><TextField label="Category" {...f('category')} placeholder="e.g. RFID reader" /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Model" {...f('model')} placeholder="e.g. IDT-9026" /></Grid>
                <Grid item xs={12}>
                  <TextField label="Specifications" {...f('specifications', { multiline: true, rows: 3 })}
                    placeholder="Frequency, range, chip, dimensions…" />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Notes" {...f('notes', { multiline: true, rows: 3 })}
                    placeholder="What impressed you, comparisons, what to ask next…" />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Supplier / Booth</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={8}><TextField label="Company" {...f('supplier_name')} /></Grid>
                <Grid item xs={12} sm={4}><TextField label="Booth No." {...f('booth_no')} placeholder="e.g. 9A-38" /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Contact Person" {...f('contact_person')} /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Phone" {...f('contact_phone')} /></Grid>
                <Grid item xs={12} sm={6}><TextField label="WeChat" {...f('wechat')} /></Grid>
                <Grid item xs={12} sm={6}><TextField label="Email" {...f('contact_email')} /></Grid>
                <Grid item xs={12}><TextField label="Website" {...f('website')} placeholder="alibaba / company site" /></Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Commercials</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={7}>
                  <TextField label="Quoted Price" type="number" {...f('price')}
                    inputProps={{ step: '0.0001', inputMode: 'decimal' }} />
                </Grid>
                <Grid item xs={5}>
                  <TextField label="Currency" select {...f('currency')}>
                    {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6}><TextField label="Per Unit" {...f('unit')} placeholder="pcs / set / roll" /></Grid>
                <Grid item xs={6}><TextField label="MOQ" {...f('moq')} placeholder="e.g. 1000 pcs" /></Grid>
                <Grid item xs={12}><TextField label="Lead Time" {...f('lead_time')} placeholder="e.g. 15 days" /></Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Where &amp; Follow-up</Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField label="Exhibition" {...f('exhibition')} placeholder="e.g. IOTE Shenzhen 2026"
                    helperText="Remembered for your next entry" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Date Seen" type="date" {...f('seen_date')} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Interest" select {...f('interest')}>
                    {INTEREST.map(o => <MenuItem key={o.v} value={o.v}>{o.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Status" select {...f('status')}>
                    {STATUSES.map(o => <MenuItem key={o.v} value={o.v}>{o.label}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Button type="submit" variant="contained" fullWidth size="large" startIcon={<Save />} disabled={busy}>
                {busy ? 'Saving…' : (id ? 'Save Changes' : 'Save Product')}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
